import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';

export default function ImportHexawarePOsPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // { [po_number]: true }
  const [vendors, setVendors] = useState([]);
  const inputRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [contractors, setContractors] = useState([]);
  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data));
    api.get('/clients').then(r => {
      setClients(r.data);
      const hexaware = r.data.find(c => c.name && c.name.toLowerCase().includes('hexaware'));
      if (hexaware) setSelectedClientId(hexaware.id);
    });
    api.get('/contractors').then(r => setContractors(r.data));
  }, []);

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setError('');
    setPreview(null);
  }

  async function runPreview() {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/purchase-orders/import/hexaware/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      setRows(r.data.matched || []);
      // Select everything by default - user unchecks what they want to hold back
      const initialSelected = {};
      (r.data.matched || []).forEach(row => { initialSelected[row.po_number] = true; });
      setSelected(initialSelected);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read this file — expected a Hexaware PO export');
    } finally {
      setLoading(false);
    }
  }

  function updateRow(idx, field, value) {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function toggleSelected(poNumber) {
    setSelected(s => ({ ...s, [poNumber]: !s[poNumber] }));
  }

  function selectAll(value) {
    const s = {};
    rows.forEach(r => { s[r.po_number] = value; });
    setSelected(s);
  }

  async function overrideContractor(idx, contractorId) {
    const row = rows[idx];
    if (!contractorId) {
      updateRow(idx, 'contractor_id', null);
      return;
    }
    try {
      const r = await api.post('/purchase-orders/import/hexaware/resolve-contractor', {
        contractor_id: Number(contractorId),
        client_wo_number: row.client_wo_number,
      });
      setRows(rs => rs.map((row2, i) => i === idx ? {
        ...row2,
        contractor_id: r.data.contractor_id,
        contractor_matched_name: r.data.contractor_matched_name,
        wo_id: r.data.wo_id,
        wo_action: r.data.wo_action,
        vendor_id: r.data.vendor_id,
        rate: r.data.rate,
        buy_rate: r.data.buy_rate,
        match_confidence: 'manual',
      } : row2));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not resolve contractor');
    }
  }

  async function runCommit() {
    setCommitting(true);
    try {
      const rowsToImport = rows
        .filter(row => selected[row.po_number])
        .map(row => ({ ...row, client_id: selectedClientId }));
      const r = await api.post('/purchase-orders/import/hexaware/confirm', { pos: rowsToImport });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
    } finally {
      setCommitting(false);
    }
  }

  function rowContractorSubtype(r) {
    const c = contractors.find(c => c.id === r.contractor_id);
    return c?.contractor_subtype || null;
  }
  function rowNeedsVendor(r) {
    return rowContractorSubtype(r) === 'corp_to_corp';
  }
  const selectedRows = rows.filter(r => selected[r.po_number]);
  const needsVendor = selectedRows.filter(r => r.wo_action === 'create_new' && rowNeedsVendor(r) && !r.vendor_id);

  return (
    <div style={{ padding: 28 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Import Hexaware PO Export</h1>
          <p className="page-subtitle">Upload a Hexaware PO export, review and edit each row, then choose which to import now.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/purchase-orders')}>Back to Purchase Orders</button>
      </div>

      {!preview && !result && (
        <>
          <div
            className={'dropzone' + (dragOver ? ' dragover' : '')}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            style={{ cursor: 'pointer', maxWidth: 700 }}
          >
            <input ref={inputRef} type="file" accept=".xls,.xlsx,.html" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
            {file ? (
              <div><strong>{file.name}</strong> selected</div>
            ) : (
              <div>
                Drag the Hexaware PO export file here, or click to browse.<br />
                <span className="form-hint">Contractor names are matched automatically (exact, then fuzzy). Review matches below before confirming.</span>
              </div>
            )}
          </div>
          {error && <div style={{ margin: '16px 0', color: 'var(--danger-text)', fontSize: 13 }}>{error}</div>}
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={runPreview} disabled={!file || loading}>
              {loading ? 'Reading file…' : 'Preview import'}
            </button>
          </div>
        </>
      )}

      {preview && !result && (
        <>
          <div className="import-summary">
            <div className="import-summary-stat">
              <div className="num">{rows.length}</div>
              <div className="label">Total rows</div>
            </div>
            <div className="import-summary-stat">
              <div className="num">{selectedRows.length}</div>
              <div className="label">Selected to import now</div>
            </div>
            <div className="import-summary-stat">
              <div className="num" style={{ color: preview.unmatched.length ? 'var(--danger-text)' : undefined }}>{preview.unmatched.length}</div>
              <div className="label">Unmatched contractor</div>
            </div>
            <div className="import-summary-stat">
              <div className="num">{preview.skipped.length}</div>
              <div className="label">Skipped</div>
            </div>
          </div>

          <div style={{ margin: '14px 0', padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)', borderLeft: '2px solid var(--primary)', maxWidth: 400 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Client for this import</label>
            <select value={selectedClientId || ''} onChange={e => setSelectedClientId(Number(e.target.value))} style={{ width: '100%' }}>
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!selectedClientId && <div style={{ fontSize: 11, color: 'var(--warning-text)', marginTop: 6 }}>Please select a client</div>}
          </div>
          {needsVendor.length > 0 && (
            <div style={{ margin: '14px 0', padding: 12, background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 'var(--radius)', fontSize: 13 }}>
              {needsVendor.length} selected row(s) need a vendor before they can create a new Work Order (Contract Placement requires one).
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, margin: '14px 0' }}>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => selectAll(true)}>Select All</button>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => selectAll(false)}>Deselect All</button>
          </div>

          {(preview.skipped.length > 0 || preview.unmatched.length > 0) && (
            <details style={{ margin: '14px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>
                View skipped ({preview.skipped.length}) and unmatched ({preview.unmatched.length}) rows
              </summary>
              <div style={{ marginTop: 10, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {preview.skipped.length > 0 && (
                  <table className="data-table" style={{ minWidth: 300 }}>
                    <thead><tr><th>PO #</th><th>Contractor</th><th>Reason</th></tr></thead>
                    <tbody>
                      {preview.skipped.map((s, i) => (
                        <tr key={i}>
                          <td>{s.po}</td>
                          <td>{s.contractor || '-'}</td>
                          <td>{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {preview.unmatched.length > 0 && (
                  <table className="data-table" style={{ minWidth: 300 }}>
                    <thead><tr><th>PO #</th><th>Contractor Name (unmatched)</th></tr></thead>
                    <tbody>
                      {preview.unmatched.map((u, i) => (
                        <tr key={i}>
                          <td>{u.po}</td>
                          <td>{u.contractor_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </details>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th></th><th>PO #</th><th>Contractor</th><th>Match</th><th>CWK #</th><th>WO</th><th>Vendor</th><th>Rate</th>
                <th>Period Start</th><th>Period End</th><th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.po_number} style={{ opacity: selected[r.po_number] ? 1 : 0.5 }}>
                  <td>
                    <input type="checkbox" checked={!!selected[r.po_number]} onChange={() => toggleSelected(r.po_number)} />
                  </td>
                  <td>{r.po_number}</td>
                  <td>
                    {r.match_confidence === 'exact' ? (
                      r.contractor_matched_name
                    ) : (
                      <div>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>
                          {r.contractor_matched_name
                            ? r.contractor_matched_name
                            : <span style={{ color: 'var(--danger-text)' }}>{r.contractor_name} (no match)</span>}
                        </div>
                        <select value={r.contractor_id || ''} onChange={e => overrideContractor(idx, e.target.value)} style={{ minWidth: 160 }}>
                          <option value="">{r.contractor_id ? 'Change contractor…' : 'Select contractor…'}</option>
                          {[...contractors].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                        </select>
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge variant={r.match_confidence === 'exact' ? 'success' : r.match_confidence === 'none' ? 'danger' : 'warning'}>
                      {r.match_confidence}
                    </Badge>
                  </td>
                  <td>{r.client_wo_number || '—'}</td>
                  <td>
                    {r.wo_action === 'existing' && <Badge variant="success">Existing</Badge>}
                    {r.wo_action === 'create_new' && <Badge variant="info">New WO</Badge>}
                    {r.wo_action === 'none' && <Badge variant="danger">No contractor</Badge>}
                  </td>
                  <td>
                    {r.wo_action === 'existing' ? (
                      r.vendor_id ? (vendors.find(v => v.id === r.vendor_id)?.name || '—') : '—'
                    ) : r.wo_action === 'create_new' && rowNeedsVendor(r) ? (
                      <select value={r.vendor_id || ''} onChange={e => updateRow(idx, 'vendor_id', Number(e.target.value))} style={{ minWidth: 140 }}>
                        <option value="">Select vendor…</option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    ) : r.wo_action === 'create_new' && rowContractorSubtype(r) ? (
                      <span className="form-hint">{rowContractorSubtype(r).toUpperCase()} — no vendor</span>
                    ) : '—'}
                  </td>
                  <td>
                    {r.wo_action === 'create_new' ? (
                      <input type="number" style={{ width: 70 }} value={r.rate || ''} onChange={e => updateRow(idx, 'rate', Number(e.target.value))} />
                    ) : (r.rate ? `$${r.rate}/hr` : '—')}
                  </td>
                  <td>
                    <input type="date" style={{ width: 130 }} value={r.period_start || ''} onChange={e => updateRow(idx, 'period_start', e.target.value)} />
                  </td>
                  <td>
                    <input type="date" style={{ width: 130 }} value={r.period_end || ''} onChange={e => updateRow(idx, 'period_end', e.target.value)} />
                  </td>
                  <td>
                    <input type="number" step="0.01" style={{ width: 70 }} value={r.hours ?? ''} onChange={e => updateRow(idx, 'hours', e.target.value === '' ? null : Number(e.target.value))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => { setPreview(null); setRows([]); setSelected({}); }}>Start Over</button>
            <button className="btn btn-primary" onClick={runCommit} disabled={committing || needsVendor.length > 0}>
              {committing ? 'Importing…' : `Import Selected (${selectedRows.length} of ${rows.length})`}
            </button>
          </div>
        </>
      )}

      {result && (
        <>
          <p>Created <strong>{result.created.length}</strong> Purchase Orders.</p>
          {result.workOrdersCreated.length > 0 && (
            <p><strong>{result.workOrdersCreated.length}</strong> new Work Order(s) were auto-created.</p>
          )}
          {result.errors.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong>{result.errors.length} row(s) had errors:</strong>
              <ul>{result.errors.map((e, i) => <li key={i} style={{ fontSize: 13 }}>{e.po}: {e.error}</li>)}</ul>
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={() => navigate('/purchase-orders')}>Done — Back to Purchase Orders</button>
          </div>
        </>
      )}
    </div>
  );
}
